import React, { memo, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Table } from 'antd'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { TemporaryDataType, TemporarySetDataType } from './useTemporaryDataState'
import { connectionColumns } from './static-data-columns'
import { EditableCell, EditableRow } from './editable-table-utils'
import { CustomDateTimePicker } from './components/CustomDateTimePicker'
import { emptyConnection } from '../utils/emptyEntities'
import { CustomTableHeader } from './components/CustomTableHeader'
import { fetchKpiGoalsWithFilter, saveAllTemporaryData } from '../importer-api'
import { deleteEntityAndItsChildren, deletedCascadeEntities } from '../utils/actions'
import { generateRestrictedGoalValue, getFormattedGoalLabel, normalizeTubularTypeValue } from '../utils/utils'
import { StaticDataImporterContext } from '../context/StaticDataImporterContext'
import { getSelectedRowKeys, useSelectAllRows } from '../hooks/useSelectAllRows'
import { useNavigateToNewRow } from '../hooks/useNavigateToNewRow'
import { RowSelectionType } from 'antd/lib/table/interface'
import { useMoveEntityRowToAnotherEntityRowParent } from '../hooks/useMoveEntityRowToAnotherEntityRowParent'

type EditableTableProps = Parameters<typeof Table>[0]

type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

const TemporaryRestrictedConnectionsTable = memo(
    ({
        parent,
        data,
        setData,
        optionsOfTubularTypeEntity,
        localErrors,
        removeErrorWhenCheckedRowIsRemoved
    }: {
        parent: TubularTypeDto
        data: TemporaryDataType
        setData: TemporarySetDataType
        optionsOfTubularTypeEntity: TubularTypeSelectOptions
        localErrors: LocalErrorsTypes[]
        removeErrorWhenCheckedRowIsRemoved: (value: TemporaryDataType) => void
    }): ReactElement => {
        const [startDate, setStartDate] = useState<Record<number, Date | string>>(null)
        const [endDate, setEndDate] = useState<Record<number, Date | string>>(null)
        const [timeOptionsByRow, setTimeOptionsByRow] = useState<
            Record<
                number,
                {
                    label: string
                    value: ValueUom | string
                }[]
            >
        >({})

        const {
            checkedRows,
            setCheckedRows,
            formatCheckedRows,
            setIsValidToTransfer,
            updateValueFromCheckedEntity
        } = useContext(StaticDataImporterContext)

        const { selectAllRows } = useSelectAllRows()

        const { goToNewRow, classNameNewRow } = useNavigateToNewRow()

        const { moveRowToAnotherParent } = useMoveEntityRowToAnotherEntityRowParent()

        const connections = useMemo(
            () =>
                data.connections
                    .filter(connec => connec.parent == parent.nodeId)
                    .map(connection => ({
                        ...connection,
                        key: String(connection.rowId),
                        parent: parent.nodeId
                    })),
            [data.connections, parent.nodeId]
        )

        useEffect(() => {
            const newStartDate: Record<number, Date | string> = {}
            const newEndDate: Record<number, Date | string> = {}
            for (const connection of data.connections) {
                newStartDate[connection.rowId] = connection.start ? new Date(connection.start) : ''
                newEndDate[connection.rowId] = connection.end ? new Date(connection.end) : ''

                if (connection?.restrictedTimeConnectionValue) {
                    connection.restrictedTimeConnection = {
                        value: connection.restrictedTimeConnectionValue,
                        name: connection.restrictedTimeConnectionUom,
                        id: 0,
                        attribute_id: null,
                        unitOfMeasure_id: null
                    }
                }
            }
            setStartDate(newStartDate)
            setEndDate(newEndDate)
        }, []) // eslint-disable-line react-hooks/exhaustive-deps

        useEffect(() => {
            // Load the Goal options for each existing connection according to its own Restriction Type.
            // Restricted-connection-type options load asynchronously in the parent, so this must re-run
            // once they arrive instead of only firing once on mount.
            connections.forEach(connection => {
                if (connection?.restrictionType) getKpiGoalOptionsWithFilter(connection)
            })
        }, [connections, getKpiGoalOptionsWithFilter])

        // Functions

        const getCurrentRigId = useCallback(
            (restrictedParent: number): string => {
                const runParent = data.runs.find(run => run.nodeId === restrictedParent).parent
                const sectionParent = data.sections.find(section => String(section.nodeId) === String(runParent)).parent
                const interventionParent = data.interventions.find(
                    intervention => intervention.nodeId === sectionParent
                ).parent
                const wellRigId = data.wells.find(well => well.nodeId === interventionParent).rigId

                return String(wellRigId)
            },
            [data.interventions, data.runs, data.sections, data.wells]
        )

        const getKpiGoalOptionsWithFilter = useCallback(
            (record: TubularRestrictedConnectionDto): void => {
                const normalizedTubularType = normalizeTubularTypeValue(parent?.tubularType)
                const tubularType = (optionsOfTubularTypeEntity?.tubularType as RestrictedSelectOptions[])?.find(
                    type => type?.value === normalizedTubularType
                )?.acronym
                const restrictedConnectionType = (optionsOfTubularTypeEntity?.restrictedConnectionType as RestrictedSelectOptions[])?.find(
                    type => type?.label === record?.restrictionType
                )?.acronym

                const filter = {
                    1: tubularType,
                    2: 'S2S',
                    7: restrictedConnectionType
                }

                const rigId = getCurrentRigId(parent.parent)

                fetchKpiGoalsWithFilter(filter, rigId).then(res => {
                    const options = []
                    res.data.map(goal => {
                        const newOption = {
                            value: goal.target?.goals?.value,
                            uom: { name: goal.target?.goals?.uom },
                            speedAcronym: getFormattedGoalLabel(goal) ?? ''
                        }
                        options.push(generateRestrictedGoalValue((newOption as unknown) as ValueUom))
                    })
                    setTimeOptionsByRow(prev => ({ ...prev, [record.rowId]: options }))
                })
            },
            [
                getCurrentRigId,
                optionsOfTubularTypeEntity?.restrictedConnectionType,
                optionsOfTubularTypeEntity?.tubularType,
                parent.parent,
                parent?.tubularType
            ]
        )

        const handleEditData = useCallback(
            (entity: EntityDto[], row: EntityDto, setter: TemporarySetDataType): void => {
                const newData = [...entity]
                const index = newData.findIndex(item => String(row.rowId) === String(item.rowId))
                const item = newData[index]
                if (item) {
                    saveAllTemporaryData(row, 'TUBULAR_RESTRICTED_CONNECTION').then(saves => {
                        row.rowId = saves[0].id
                        row.key = saves[0].id
                        newData.splice(index, 1, {
                            ...item,
                            ...row
                        })
                        setter.setConnections(newData as TubularRestrictedConnectionDto[])
                        setIsValidToTransfer(false)
                        updateValueFromCheckedEntity(`connections=>${row.parent}`, row)
                    })
                }
            },
            [setIsValidToTransfer, updateValueFromCheckedEntity]
        )

        const handleEditSave = useCallback(
            (row: EntityDto): void => {
                handleEditData(data.connections, row, setData)
                getKpiGoalOptionsWithFilter(row as TubularRestrictedConnectionDto)
            },
            [data.connections, getKpiGoalOptionsWithFilter, handleEditData, setData]
        )

        const setRowStartDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.connections]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                currentRow.start = newDate ? new Date(newDate).getTime() : null
                handleEditSave(currentRow)
                // Changing startDate state
                const newStartDate = { ...startDate }
                newStartDate[rowId] = newDate
                setStartDate(newStartDate)
            },
            [data.connections, handleEditSave, startDate]
        )

        const setRowEndDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.connections]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                currentRow.end = newDate ? new Date(newDate).getTime() : null
                handleEditSave(currentRow)
                // Changing endDate state
                const newEndDate = { ...endDate }
                newEndDate[rowId] = newDate
                setEndDate(newEndDate)
            },
            [data.connections, endDate, handleEditSave]
        )

        const handleAddNewRow = useCallback((): void => {
            const oldEntity = [...data.connections]

            const newEntity: TubularRestrictedConnectionDto = {
                ...emptyConnection,
                key: undefined,
                frontParentUuid: parent.frontUuid,
                parent: parent.nodeId,
                nodeId: Date.now(),
                rowId: undefined,
                frontUuid: uuidv4()
            }

            saveAllTemporaryData(newEntity, 'TUBULAR_RESTRICTED_CONNECTION').then(saves => {
                newEntity.rowId = saves[0].id
                newEntity.key = saves[0].id
                newEntity.newRow = true
                oldEntity.push(newEntity)
                setData.setConnections(oldEntity)
                goToNewRow()
            })
        }, [data.connections, goToNewRow, parent.frontUuid, parent.nodeId, setData])

        const handleDelete = useCallback(
            (record: TubularRestrictedConnectionDto): void => {
                const newData = data.connections.filter(item => String(item.rowId) !== String(record.rowId))
                const newCheckedRows = deletedCascadeEntities(checkedRows, record)

                deleteEntityAndItsChildren(record.rowId).then(() => {
                    const newCheckedToTransfer = formatCheckedRows(newCheckedRows)

                    setData.setConnections(newData)
                    setCheckedRows(newCheckedRows)
                    removeErrorWhenCheckedRowIsRemoved(newCheckedToTransfer)
                })
            },
            [
                checkedRows,
                data.connections,
                formatCheckedRows,
                removeErrorWhenCheckedRowIsRemoved,
                setCheckedRows,
                setData
            ]
        )

        // Element components
        const handleCalendarElementStartDate = useCallback(
            (record: TubularRestrictedConnectionDto): ReactElement => {
                if (!startDate) return null

                return (
                    <CustomDateTimePicker
                        date={startDate[record.rowId] as string}
                        setDate={setRowStartDate}
                        rowId={record.rowId}
                    />
                )
            },
            [setRowStartDate, startDate]
        )

        const handleCalendarElementEndDate = useCallback(
            (record: TubularRestrictedConnectionDto): ReactElement => {
                if (!endDate) return null

                return (
                    <CustomDateTimePicker
                        date={endDate[record.rowId] as string}
                        setDate={setRowEndDate}
                        rowId={record.rowId}
                    />
                )
            },
            [endDate, setRowEndDate]
        )

        const components = useMemo(
            () => ({
                body: {
                    row: EditableRow,
                    cell: EditableCell
                }
            }),
            []
        )

        const connectionsCols = useMemo(
            () =>
                connectionColumns(
                    handleDelete,
                    handleEditSave,
                    optionsOfTubularTypeEntity,
                    (record: EntityDto, selectedParent: number) =>
                        moveRowToAnotherParent(record, selectedParent, handleEditSave),
                    data.tubulars,
                    handleCalendarElementStartDate,
                    handleCalendarElementEndDate,
                    localErrors,
                    timeOptionsByRow
                ),
            [
                data.tubulars,
                handleCalendarElementEndDate,
                handleCalendarElementStartDate,
                handleDelete,
                handleEditSave,
                localErrors,
                moveRowToAnotherParent,
                optionsOfTubularTypeEntity,
                timeOptionsByRow
            ]
        )

        const title = useCallback((): ReactElement => {
            return <CustomTableHeader title="restricted connections" handleAddNewRow={handleAddNewRow} />
        }, [handleAddNewRow])

        const rowSelection = useMemo(
            () => ({
                selectedRowKeys: getSelectedRowKeys(checkedRows, 'connections', parent),
                type: 'checkbox' as RowSelectionType,
                onChange: (selectedRowsKey: React.Key[], selectedRows: TubularRestrictedConnectionDto[]) => {
                    selectAllRows({
                        data,
                        selectedRows,
                        parentNodeId: parent.nodeId,
                        type: 'connections'
                    })
                },
                onSelect: (
                    record: EntityDto,
                    selected: boolean,
                    selectedRows: TubularRestrictedConnectionDto[],
                    nativeEvent: Event
                ) => {
                    selectAllRows({
                        data,
                        parentNodeId: parent.nodeId,
                        type: 'connections',
                        selectedRows,
                        target: record,
                        selected,
                        nativeEvent
                    })
                }
            }),
            [checkedRows, data, parent, selectAllRows]
        )

        const expandable = useMemo(
            () => ({
                defaultExpandAllRows: false
            }),
            []
        )

        return (
            <TableContainer>
                <Table
                    title={title}
                    size="small"
                    components={components}
                    rowClassName={(d: EntityDto) => (d?.newRow ? `editable-row ${classNameNewRow}` : 'editable-row')}
                    bordered
                    dataSource={connections}
                    columns={connectionsCols as ColumnTypes}
                    pagination={false}
                    rowSelection={rowSelection}
                    expandable={expandable}
                />
            </TableContainer>
        )
    }
)

TemporaryRestrictedConnectionsTable.displayName = 'TemporaryRestrictedConnectionsTable'

export default TemporaryRestrictedConnectionsTable

const TableContainer = styled.main``
